import { Bar, Chart } from "react-chartjs-2";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LineElement,
  LinearScale,
  PointElement,
  Tooltip,
  type ChartOptions
} from "chart.js";
import { useEffect, useId, useMemo, useState, type ReactNode } from "react";
import { PermissionGate } from "../../components/PermissionGate";
import { StatusMessage } from "../../components/StatusMessage";
import { chartValueLabelsPlugin, withValueLabels } from "../../components/chartValueLabels";
import { apiGet } from "../../services/api";
import { useAuth } from "../../services/auth";
import type { EvasaoEvent, EvasaoSnapshot } from "../../types/evasao";
import {
  ANALYSIS_START_PERIOD,
  EMPTY_EVASAO_FILTERS,
  activeFilterCount,
  buildEvasaoOptions,
  buildEvasaoSummary,
  eventCourse,
  eventPeriod,
  eventReason,
  eventUa,
  formatDays,
  formatEventDate,
  formatNumber,
  formatPercent,
  formatSnapshotDate,
  fullTrend,
  hasCancellationService,
  periodRank,
  searchEvents,
  trendComparison,
  type EvasaoFilters,
  type RankedItem
} from "./evasaoModel";
import "./evasao.css";

ChartJS.register(CategoryScale, LinearScale, BarElement, LineElement, PointElement, Tooltip, Legend, chartValueLabelsPlugin);

const SNAPSHOT_ENDPOINT = "/api/snapshots/evasao";
const INITIAL_TABLE_ROWS = 25;
const MORE_TABLE_ROWS = 25;
const FILTER_LABELS: Record<keyof EvasaoFilters, string> = {
  periodo: "Período letivo",
  curso: "Curso",
  ua: "Unidade acadêmica",
  motivo: "Motivo"
};

const horizontalBarOptions: ChartOptions<"bar"> = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 450 },
  indexAxis: "y",
  layout: { padding: { right: 8 } },
  plugins: {
    legend: { display: false },
    tooltip: { backgroundColor: "#102a43", padding: 12, displayColors: false }
  },
  scales: {
    x: {
      display: false,
      beginAtZero: true,
      border: { display: false },
      grid: { display: false, drawTicks: false },
      ticks: { display: false }
    },
    y: {
      border: { display: false },
      grid: { display: false },
      ticks: { color: "#334155", font: { size: 11, weight: 600 } }
    }
  }
};

const verticalBarOptions: ChartOptions<"bar"> = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 450 },
  layout: { padding: { top: 18, right: 8, left: 8 } },
  plugins: {
    legend: { display: false },
    tooltip: { backgroundColor: "#102a43", padding: 12, displayColors: false }
  },
  scales: {
    x: {
      border: { display: false },
      grid: { display: false },
      ticks: { color: "#475569", font: { size: 11, weight: 600 } }
    },
    y: {
      display: false,
      beginAtZero: true,
      border: { display: false },
      grid: { display: false, drawTicks: false },
      ticks: { display: false }
    }
  }
};

export function EvasaoPage() {
  const { token } = useAuth();
  const [snapshot, setSnapshot] = useState<EvasaoSnapshot | null>(null);
  const [filters, setFilters] = useState<EvasaoFilters>(EMPTY_EVASAO_FILTERS);
  const [query, setQuery] = useState("");
  const [visibleRows, setVisibleRows] = useState(INITIAL_TABLE_ROWS);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    setError("");
    const injectedSnapshot = import.meta.env.DEV
      ? (window as typeof window & { __EVASAO_SNAPSHOT__?: EvasaoSnapshot }).__EVASAO_SNAPSHOT__
      : undefined;
    if (!injectedSnapshot && !token) return;
    const snapshotRequest = injectedSnapshot
      ? Promise.resolve(injectedSnapshot)
      : apiGet<EvasaoSnapshot>(SNAPSHOT_ENDPOINT, token || undefined);

    void snapshotRequest
      .then((payload) => {
        if (!payload) throw new Error("Snapshot de evasão não encontrado.");
        setSnapshot(payload);
      })
      .catch((err: unknown) => setError(err instanceof Error ? err.message : "Falha ao carregar os dados de evasão."))
      .finally(() => setLoading(false));
  }, [token]);

  const options = useMemo(() => snapshot ? buildEvasaoOptions(snapshot) : {
    periodo: [],
    curso: [],
    ua: [],
    motivo: []
  }, [snapshot]);
  const summary = useMemo(() => snapshot ? buildEvasaoSummary(snapshot, filters) : null, [filters, snapshot]);
  const trend = useMemo(() => snapshot ? fullTrend(snapshot) : [], [snapshot]);
  const comparison = useMemo(() => snapshot ? trendComparison(snapshot) : null, [snapshot]);
  const matchingRows = useMemo(() => summary
    ? searchEvents(summary.events, query).sort(sortEventsNewestFirst)
    : [], [query, summary]);

  function updateFilter(key: keyof EvasaoFilters, value: string) {
    setFilters((current) => ({ ...current, [key]: value }));
    setVisibleRows(INITIAL_TABLE_ROWS);
  }

  function resetFilters() {
    setFilters(EMPTY_EVASAO_FILTERS);
    setQuery("");
    setVisibleRows(INITIAL_TABLE_ROWS);
  }

  const activeFilters = activeFilterCount(filters);
  const partialPeriod = comparison?.partialPeriod;
  const reasonTop = summary?.reasons[0];
  const trendAuditNote = snapshot
    ? buildTrendAuditNote(snapshot, trend, filters.periodo, partialPeriod)
    : null;

  return (
    <PermissionGate permission="evasao">
      <div className="evasao-page">
        <h1 className="sr-only">Evasão estudantil</h1>

        {loading ? <DashboardSkeleton /> : null}
        {error ? <StatusMessage tone="danger">{error}</StatusMessage> : null}

        {snapshot && summary ? (
          <>
            <section className="evasao-filters" aria-labelledby="evasao-filter-title">
              <div className="evasao-filters__head">
                <div>
                  <p className="section-kicker">Recorte analítico</p>
                  <h2 id="evasao-filter-title">Explore os dados</h2>
                </div>
                {activeFilters ? (
                  <div className="evasao-filters__status">
                    <span className="filter-count filter-count--active">
                      {`${activeFilters} filtro${activeFilters > 1 ? "s" : ""} ativo${activeFilters > 1 ? "s" : ""}`}
                    </span>
                  </div>
                ) : null}
              </div>
              <div className="evasao-filters__grid">
                {(Object.keys(FILTER_LABELS) as Array<keyof EvasaoFilters>).map((key) => (
                  <label className="evasao-field" key={key}>
                    <span>{FILTER_LABELS[key]}</span>
                    <select value={filters[key]} onChange={(event) => updateFilter(key, event.target.value)}>
                      <option value="">Todos</option>
                      {options[key].map((option) => (
                        <option key={option.value} value={option.value}>{option.label}</option>
                      ))}
                    </select>
                  </label>
                ))}
              </div>
              <div className="evasao-filters__actions">
                <button type="button" className="evasao-button evasao-button--ghost" onClick={resetFilters} disabled={!activeFilters && !query}>
                  <Icon name="reset" /> Limpar recorte
                </button>
                <button type="button" className="evasao-button evasao-button--primary" onClick={() => downloadCsv(summary.events)} disabled={!summary.total}>
                  <Icon name="download" /> Exportar CSV
                </button>
              </div>
            </section>

            <section className="evasao-metrics" aria-label="Indicadores principais">
              <MetricCard label="Eventos no recorte" value={formatNumber(summary.total)} detail={`Base analítica desde ${ANALYSIS_START_PERIOD}`} help={`Contagem dos registros de cancelamento que atendem aos filtros, sempre considerando ${ANALYSIS_START_PERIOD} em diante.`} tone="red" icon="students" />
              <MetricCard label="Motivo identificado" value={formatPercent(summary.reasonCoverage)} detail={`${formatNumber(summary.identifiedReasonCount)} de ${formatNumber(summary.total)} eventos`} help="Eventos com motivo preenchido divididos pelo total de eventos do recorte, multiplicados por 100." tone="blue" icon="target" />
              <MetricCard label="Com atendimento" value={formatPercent(summary.attendedRate)} detail={`${formatNumber(summary.attendedCount)} registros vinculados`} help="Eventos com atendimento vinculado ou quantidade de atendimentos maior que zero, divididos pelo total do recorte." tone="teal" icon="service" />
              <MetricCard label="Lifetime mediano" value={formatDays(summary.lifetimeMedianDays)} detail={`${formatNumber(summary.lifetimeCount)} casos com data válida`} help="Mediana dos dias entre o ingresso de referência e o cancelamento, considerando apenas datas válidas." tone="gold" icon="clock" />
              <MetricCard label="Evasão em até 90 dias" value={formatPercent(summary.earlyExitRate)} detail={`${formatNumber(summary.earlyExitCount)} casos com lifetime`} help="Casos com lifetime entre 0 e 90 dias divididos pelos casos com lifetime válido, multiplicados por 100." tone="purple" icon="bolt" />
            </section>

            <section className="evasao-overview-grid">
              <AnalyticsPanel
                className="analytics-panel--trend"
                kicker="Série histórica"
                title="Tendência de evasão"
                subtitle="Todos os semestres disponíveis; barras mostram eventos e a linha mostra a taxa"
                help={`${snapshot.metodologia_tendencia || "A tendência consolida as linhas por estudante e semestre, remove duplicidades e desconsidera quem voltou a ter vínculo ativo depois do cancelamento."} A taxa divide as evasões válidas pelo total de alunos do mesmo semestre e multiplica por 100. A série mantém todos os semestres disponíveis e sinaliza o período parcial.`}
                badge={partialPeriod ? `${partialPeriod} parcial` : undefined}
              >
                {trend.length ? (
                  <div className="trend-chart-scroll">
                    <div className="trend-chart" role="img" aria-label="Gráfico histórico de eventos e taxa de evasão por semestre">
                      <Chart
                        type="bar"
                        data={{
                          labels: trend.map((row) => row.periodo_letivo || "—"),
                          datasets: [
                            withValueLabels<"bar">({
                              type: "bar" as const,
                              label: "Eventos de evasão",
                              data: trend.map((row) => Number(row.total_evasao || 0)),
                              backgroundColor: trend.map((row) => row.periodo_letivo === partialPeriod ? "rgba(217, 154, 27, 0.72)" : "rgba(29, 68, 108, 0.72)"),
                              borderColor: trend.map((row) => row.periodo_letivo === partialPeriod ? "#d99a1b" : "#1d446c"),
                              borderWidth: 1,
                              borderRadius: 4,
                              borderSkipped: false,
                              maxBarThickness: 24,
                              yAxisID: "yEvents",
                              order: 2
                            }, trend.map((row) => formatNumber(Number(row.total_evasao || 0)))),
                            withValueLabels<"line">({
                              type: "line" as const,
                              label: "Taxa de evasão (%)",
                              data: trend.map((row) => Number(row.taxa_evasao || 0)),
                              borderColor: "#c8102e",
                              backgroundColor: "#c8102e",
                              borderWidth: 3.5,
                              pointBackgroundColor: trend.map((row) => row.periodo_letivo === partialPeriod ? "#d99a1b" : "#c8102e"),
                              pointBorderColor: "#ffffff",
                              pointBorderWidth: 3,
                              pointRadius: trend.map((row) => row.periodo_letivo === partialPeriod ? 7 : 5),
                              pointHoverRadius: 8,
                              tension: 0.28,
                              yAxisID: "yRate",
                              order: 1
                            }, trend.map((row) => formatPercent(Number(row.taxa_evasao || 0), 2)))
                          ]
                        }}
                        options={trendOptions}
                      />
                    </div>
                  </div>
                ) : <EmptyState label="Sem série histórica disponível." />}
                {trendAuditNote ? (
                  <p className="trend-audit-note" aria-live="polite" aria-atomic="true">
                    <Icon name="info" /> {trendAuditNote}
                  </p>
                ) : null}
                <div className="chart-footnote">
                  <span className="chart-footnote__dot chart-footnote__dot--partial" />
                  {partialPeriod ? `${partialPeriod} é parcial até ${formatSnapshotDate(snapshot.data_geracao)} e não entra na comparação entre períodos encerrados.` : "A série contém apenas períodos encerrados."}
                </div>
              </AnalyticsPanel>

            </section>

            {summary.total ? (
              <section className="evasao-analytics-grid" aria-label="Análises do recorte">
                <AnalyticsPanel kicker="Razões declaradas" title="Motivos de cancelamento" subtitle={`${formatPercent(summary.reasonCoverage)} dos eventos possuem motivo identificado`} help="Cada barra conta os eventos de uma categoria. O percentual usa todos os eventos do recorte, inclusive Sem motivo registrado; a classificação vem pronta do gerar_relatorio.py.">
                  <ChartFrame label="Distribuição das categorias de motivo de cancelamento">
                    <Bar data={rankedChartData(summary.reasons.slice(0, 7), ["#c8102e", "#1d446c", "#0f766e", "#7c3aed", "#d99a1b", "#64748b", "#94a3b8"])} options={horizontalBarOptions} />
                  </ChartFrame>
                  <PanelNote>{reasonTop ? `${reasonTop.label} representa ${formatPercent(reasonTop.share)} dos eventos no recorte.` : "Nenhum evento no recorte."}</PanelNote>
                </AnalyticsPanel>

                <AnalyticsPanel kicker="Ciclo de vida" title="Tempo até a evasão" subtitle={`${formatNumber(summary.lifetimeCount)} registros com lifetime calculado`} help="O tempo é a quantidade de dias entre o ingresso de referência e o cancelamento. O percentual de cada faixa usa somente os registros com as duas datas válidas.">
                  <ChartFrame label="Distribuição de eventos por faixa de tempo até a evasão">
                    <Bar
                      data={rankedChartData(summary.lifetimeBuckets, ["#c8102e", "#e0576e", "#d99a1b", "#1d446c", "#0f766e"], 48)}
                      options={verticalBarOptions}
                    />
                  </ChartFrame>
                  <PanelNote>Média de {formatDays(summary.lifetimeAverageDays)} e mediana de {formatDays(summary.lifetimeMedianDays)} no recorte.</PanelNote>
                </AnalyticsPanel>

                <AnalyticsPanel kicker="Concentração" title="Cursos com maior volume" subtitle="Ranking por eventos de evasão, não por taxa" help="Cada barra conta os eventos associados ao curso. O percentual divide essa quantidade pelo total de eventos do recorte; não é uma taxa sobre matriculados do curso.">
                  <ChartFrame label="Cursos com maior número de eventos de evasão">
                    <Bar data={rankedChartData(summary.courses.slice(0, 8), "#1d446c")} options={horizontalBarOptions} />
                  </ChartFrame>
                  <PanelNote>O volume ajuda a priorizar investigação; compare também com o total de matriculados antes de concluir risco.</PanelNote>
                </AnalyticsPanel>

                <AnalyticsPanel kicker="Distribuição" title="Eventos por unidade acadêmica" subtitle="Participação no recorte selecionado" help="Cada barra conta os eventos associados à unidade acadêmica. O percentual divide essa quantidade pelo total de eventos do recorte atual.">
                  <ChartFrame label="Distribuição dos eventos de evasão por unidade acadêmica">
                    <Bar data={rankedChartData(summary.uas, ["#1d446c", "#c8102e", "#0f766e", "#d99a1b", "#7c3aed"])} options={horizontalBarOptions} />
                  </ChartFrame>
                </AnalyticsPanel>
              </section>
            ) : (
              <StatusMessage tone="warning">Nenhum evento corresponde ao recorte selecionado. Limpe um ou mais filtros para ampliar a análise.</StatusMessage>
            )}

            <section className="evasao-table-panel" aria-labelledby="evasao-table-title">
              <div className="evasao-table-panel__head">
                <div>
                  <p className="section-kicker">Detalhamento</p>
                  <h2 id="evasao-table-title">Registros do recorte</h2>
                  <p>{formatNumber(matchingRows.length)} resultado{matchingRows.length === 1 ? "" : "s"}; dados desde {ANALYSIS_START_PERIOD}</p>
                </div>
                <label className="table-search">
                  <span className="sr-only">Buscar nos registros</span>
                  <Icon name="search" />
                  <input type="search" value={query} onChange={(event) => { setQuery(event.target.value); setVisibleRows(INITIAL_TABLE_ROWS); }} placeholder="Buscar RA, curso, UA ou motivo" />
                </label>
              </div>
              <div className="evasao-table-wrap">
                <table className="evasao-table">
                  <thead>
                    <tr>
                      <th scope="col">RA</th><th scope="col">Período</th><th scope="col">Curso</th><th scope="col">UA</th><th scope="col">Motivo</th><th scope="col">Data ref.</th><th scope="col">Lifetime</th><th scope="col">Atendimento</th>
                    </tr>
                  </thead>
                  <tbody>
                    {matchingRows.length ? matchingRows.slice(0, visibleRows).map((row, index) => (
                      <tr key={`${String(row.ra || "sem-ra")}-${eventPeriod(row)}-${index}`}>
                        <td className="evasao-table__ra">{String(row.ra || "—")}</td>
                        <td><span className="period-chip">{eventPeriod(row)}</span></td>
                        <td>{eventCourse(row)}</td>
                        <td><span className="ua-chip">{eventUa(row)}</span></td>
                        <td><span className={`reason-chip ${reasonClass(eventReason(row))}`}>{eventReason(row)}</span></td>
                        <td>{formatEventDate(row.data_cancelamento_referencia)}</td>
                        <td>{row.lifetime_dias == null ? "—" : formatDays(Number(row.lifetime_dias))}</td>
                        <td><span className={`service-chip ${hasCancellationService(row) ? "service-chip--yes" : "service-chip--no"}`}>{hasCancellationService(row) ? "Vinculado" : "Sem vínculo"}</span></td>
                      </tr>
                    )) : <tr><td colSpan={8} className="evasao-table__empty">Nenhum registro encontrado.</td></tr>}
                  </tbody>
                </table>
              </div>
              {matchingRows.length > visibleRows ? (
                <div className="evasao-table-panel__more">
                  <span>Exibindo {formatNumber(visibleRows)} de {formatNumber(matchingRows.length)}</span>
                  <button type="button" className="evasao-button evasao-button--ghost" onClick={() => setVisibleRows((current) => current + MORE_TABLE_ROWS)}>Mostrar mais</button>
                </div>
              ) : null}
            </section>
          </>
        ) : !loading && !error ? <StatusMessage tone="warning">Nenhum snapshot disponível em {SNAPSHOT_ENDPOINT}.</StatusMessage> : null}
      </div>
    </PermissionGate>
  );
}

const trendOptions: ChartOptions<"bar" | "line"> = {
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 500 },
  interaction: { mode: "index", intersect: false },
  layout: { padding: { top: 22, right: 12, left: 12 } },
  plugins: {
    legend: { position: "top", align: "start", labels: { usePointStyle: true, pointStyle: "circle", boxWidth: 8, color: "#334155", padding: 18 } },
    tooltip: { backgroundColor: "#102a43", padding: 12, usePointStyle: true }
  },
  scales: {
    x: { border: { display: false }, grid: { display: false }, ticks: { autoSkip: false, maxRotation: 45, minRotation: 0, color: "#64748b", font: { size: 10 } } },
    yEvents: { display: false, type: "linear", position: "left", beginAtZero: true, border: { display: false }, grid: { display: false, drawTicks: false }, title: { display: false }, ticks: { display: false } },
    yRate: { display: false, type: "linear", position: "right", beginAtZero: true, border: { display: false }, grid: { display: false, drawTicks: false }, title: { display: false }, ticks: { display: false } }
  }
};

function AnalyticsPanel({ kicker, title, subtitle, help, badge, className = "", children }: { kicker: string; title: string; subtitle?: string; help?: string; badge?: string; className?: string; children: ReactNode }) {
  const helpId = useId();
  return (
    <article className={`analytics-panel ${className}`}>
      <div className="analytics-panel__head">
        <div><p className="section-kicker">{kicker}</p><h2>{title}</h2>{subtitle ? <p>{subtitle}</p> : null}</div>
        {help || badge ? (
          <div className="analytics-panel__tools">
            {help ? (
              <span className="analytics-panel__help">
                <button type="button" aria-label={`Como é calculado: ${title}`} aria-describedby={helpId}><Icon name="info" /></button>
                <span id={helpId} role="tooltip">{help}</span>
              </span>
            ) : null}
            {badge ? <span className="panel-badge">{badge}</span> : null}
          </div>
        ) : null}
      </div>
      <div className="analytics-panel__body">{children}</div>
    </article>
  );
}

function MetricCard({ label, value, detail, help, tone, icon }: { label: string; value: string; detail: string; help: string; tone: "red" | "blue" | "teal" | "gold" | "purple"; icon: IconName }) {
  const helpId = `evasao-kpi-${label.normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-zA-Z0-9]+/g, "-").toLowerCase()}`;
  return (
    <article className={`evasao-metric evasao-metric--${tone}`}>
      <div className="evasao-metric__top">
        <span>{label}</span>
        <span className="evasao-metric__help">
          <button type="button" className="evasao-metric__icon" aria-label={`Entenda o cálculo de ${label}`} aria-describedby={helpId}><Icon name={icon} /></button>
          <span id={helpId} role="tooltip" className="evasao-metric__tooltip">{help}</span>
        </span>
      </div>
      <strong>{value}</strong>
      <small>{detail}</small>
    </article>
  );
}

function ChartFrame({ label, children }: { label: string; children: ReactNode }) {
  return <div className="chart-frame" role="img" aria-label={label}>{children}</div>;
}

function PanelNote({ children }: { children: ReactNode }) {
  return <p className="panel-note"><Icon name="info" /> {children}</p>;
}

function EmptyState({ label }: { label: string }) {
  return <div className="chart-empty"><Icon name="chart" /><span>{label}</span></div>;
}

function DashboardSkeleton() {
  return <div className="evasao-skeleton" aria-label="Carregando painel de evasão" aria-busy="true"><div className="evasao-skeleton__line" /><div className="evasao-skeleton__cards">{Array.from({ length: 5 }, (_, index) => <div key={index} />)}</div><div className="evasao-skeleton__panel" /></div>;
}

function rankedChartData(items: RankedItem[], colors: string | string[], maxBarThickness = 34) {
  return {
    labels: items.map((item) => shortLabel(item.label)),
    datasets: [withValueLabels<"bar">({
      data: items.map((item) => item.value),
      backgroundColor: colors,
      borderRadius: 8,
      borderSkipped: false as const,
      maxBarThickness
    }, items.map((item) => `${formatNumber(item.value)} · ${formatPercent(item.share)}`))]
  };
}

function shortLabel(value: string, max = 34): string {
  return value.length > max ? `${value.slice(0, max - 1)}…` : value;
}

function sortEventsNewestFirst(a: EvasaoEvent, b: EvasaoEvent): number {
  const dateOrder = String(b.data_cancelamento_referencia || "").localeCompare(String(a.data_cancelamento_referencia || ""));
  if (dateOrder) return dateOrder;
  return periodRank(eventPeriod(b)) - periodRank(eventPeriod(a));
}

function reasonClass(reason: string): string {
  const normalized = reason.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  if (normalized.includes("finance")) return "reason-chip--finance";
  if (normalized.includes("academ")) return "reason-chip--academic";
  if (normalized.includes("tempo") || normalized.includes("trabalho")) return "reason-chip--time";
  if (normalized.includes("nao informado") || normalized.includes("sem motivo registrado")) return "reason-chip--missing";
  return "reason-chip--other";
}

function downloadCsv(events: EvasaoEvent[]) {
  const headers = ["RA", "Período", "Curso", "UA", "Polo", "Motivo", "Data de referência", "Lifetime (dias)", "Atendimentos"];
  const rows = events.map((row) => [row.ra, eventPeriod(row), eventCourse(row), eventUa(row), row.campus_polo, eventReason(row), formatEventDate(row.data_cancelamento_referencia), row.lifetime_dias, Number(row.qtd_atendimentos_cancelamento || 0)]);
  const csv = [headers, ...rows].map((row) => row.map(csvCell).join(";")).join("\r\n");
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = `evasao-${ANALYSIS_START_PERIOD.replace("/", "-")}.csv`;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.setTimeout(() => URL.revokeObjectURL(url), 0);
}

function csvCell(value: unknown): string {
  const text = String(value ?? "");
  return `"${text.replace(/"/g, '""')}"`;
}

function buildTrendAuditNote(
  snapshot: EvasaoSnapshot,
  trend: ReturnType<typeof fullTrend>,
  selectedPeriod?: string,
  partialPeriod?: string | null
): string | null {
  const audits = snapshot.auditoria_tendencia || [];
  const audit = selectedPeriod
    ? audits.find((row) => row.periodo_letivo === selectedPeriod)
    : [...audits]
      .sort((left, right) => periodRank(right.periodo_letivo || "") - periodRank(left.periodo_letivo || ""))
      .find((row) => row.periodo_letivo !== partialPeriod);
  if (!audit?.periodo_letivo) return null;

  const metric = trend.find((row) => row.periodo_letivo === audit.periodo_letivo);
  const cancelledRows = Number(audit.linhas_canceladas_base || 0);
  const duplicates = Number(audit.duplicidades_removidas || 0);
  const laterReturns = Number(audit.retornos_posteriores_removidos || 0);
  const shiftedOut = Number(audit.saidas_por_periodo_analitico || 0);
  const shiftedIn = Number(audit.entradas_por_periodo_analitico || 0);
  const total = Number(audit.total_evasao ?? metric?.total_evasao ?? 0);
  const rate = Number(metric?.taxa_evasao || 0);
  const reportedDenominator = Number(metric?.total_alunos || 0);
  const denominator = reportedDenominator || (rate > 0 ? Math.round(total / (rate / 100)) : 0);
  const adjustments = [
    shiftedOut ? ` − ${formatNumber(shiftedOut)} ajustes para outro semestre` : "",
    shiftedIn ? ` + ${formatNumber(shiftedIn)} ajustes vindos de outro semestre` : ""
  ].join("");
  const rateCalculation = denominator
    ? ` Taxa: ${formatNumber(total)} ÷ ${formatNumber(denominator)} = ${formatPercent(rate, 2)}.`
    : "";

  return `${audit.periodo_letivo}: ${formatNumber(cancelledRows)} linhas canceladas − ${formatNumber(duplicates)} duplicidades por multivínculo − ${formatNumber(laterReturns)} retornos posteriores${adjustments} = ${formatNumber(total)} evasões.${rateCalculation}`;
}

type IconName = "info" | "reset" | "download" | "students" | "target" | "service" | "clock" | "bolt" | "search" | "chart";

function Icon({ name }: { name: IconName }) {
  const paths: Record<IconName, ReactNode> = {
    info: <><circle cx="12" cy="12" r="9" /><path d="M12 10v6M12 7h.01" /></>,
    reset: <><path d="M4 4v6h6M4.6 10A8 8 0 1 1 6 18" /></>,
    download: <><path d="M12 3v12M7 10l5 5 5-5M4 20h16" /></>,
    students: <><circle cx="9" cy="8" r="3" /><path d="M3.5 20v-2.5A4.5 4.5 0 0 1 8 13h2a4.5 4.5 0 0 1 4.5 4.5V20M15 5.5a3 3 0 0 1 0 5.8M17 14a4.5 4.5 0 0 1 3.5 4.4V20" /></>,
    target: <><circle cx="12" cy="12" r="8" /><circle cx="12" cy="12" r="3" /><path d="m14 10 6-6M16 4h4v4" /></>,
    service: <><path d="M4 13v-2a8 8 0 0 1 16 0v2M4 13h3v6H5a1 1 0 0 1-1-1v-5ZM20 13h-3v6h2a1 1 0 0 0 1-1v-5ZM17 19c0 1.1-1.8 2-4 2" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3.5 2" /></>,
    bolt: <><path d="m13 2-8 12h7l-1 8 8-12h-7l1-8Z" /></>,
    search: <><circle cx="11" cy="11" r="7" /><path d="m20 20-4-4" /></>,
    chart: <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>
  };
  return <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">{paths[name]}</svg>;
}
